import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../config/axiosConfig';
import useFetch from '../../hooks/useFetch';
import usePolling from '../../hooks/usePolling';
import RTL from '../../utils/RTL';
import { toEnglishDigits, toPersianDigits } from '../../utils/convertNumbers';
import { formatJalaliDateTime } from '../../utils/formatJalaliDate';
import { API } from '../../utils/apiRoutes';
import FloatingSnackbar from '../common/FloatingSnackbar';

const AI_REQUEST_TIMEOUT_MS = 60000;
const POLL_INTERVAL_MS = 15000;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const IMAGE_TOO_LARGE_MESSAGE = 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد.';

const STATUS_LABELS = {
    ai_handling: 'در حال پاسخگویی هوش مصنوعی',
    escalated: 'ارجاع به پشتیبان',
    resolved: 'حل شده',
    closed: 'بسته شده',
};

function extractApiError(err, fallback = 'خطا در انجام درخواست') {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 429) {
        return 'تعداد پیام‌های شما زیاد است؛ کمی بعد دوباره تلاش کنید.';
    }
    if (status === 503 && detail) {
        return detail;
    }
    return detail || fallback;
}

function MessageBubble({ message }) {
    const isUser = message.role === 'user';
    const isStaff = message.role === 'staff';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-start' : 'flex-end',
                mb: 0.75,
            }}
        >
            {isStaff && (
                <Chip
                    size="small"
                    icon={<SupportAgentIcon />}
                    label="پشتیبان انسانی"
                    color="secondary"
                    sx={{ mb: 0.25, height: 20, '& .MuiChip-label': { fontSize: '0.65rem' } }}
                />
            )}
            <Paper
                elevation={0}
                sx={{
                    px: 1.5,
                    py: 0.75,
                    maxWidth: { xs: '85%', sm: '70%' },
                    bgcolor: isUser ? 'primary.main' : isStaff ? 'secondary.light' : 'background.neutral',
                    color: isUser ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2,
                }}
            >
                {message.image && (
                    <Box
                        component="img"
                        src={message.image}
                        alt="تصویر پیوست"
                        onClick={() => window.open(message.image, '_blank', 'noopener')}
                        sx={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 240,
                            borderRadius: 1.5,
                            mb: message.content ? 1 : 0,
                            cursor: 'pointer',
                        }}
                    />
                )}
                {message.content && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.25,
                        opacity: 0.65,
                        fontSize: '0.65rem',
                        textAlign: isUser ? 'left' : 'right',
                    }}
                >
                    {formatJalaliDateTime(message.created_at)}
                </Typography>
            </Paper>
        </Box>
    );
}

function ConversationListPanel({
    conversations,
    loading,
    selectedId,
    onSelect,
    onNewChat,
}) {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, pb: 1 }}>
                <Button fullWidth variant="outlined" onClick={onNewChat}>
                    گفتگوی جدید
                </Button>
            </Box>
            <Divider />
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <List sx={{ flex: 1, overflowY: 'auto' }}>
                    {conversations.length === 0 ? (
                        <Typography align="center" variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            هنوز گفتگویی ندارید.
                        </Typography>
                    ) : (
                        conversations.map((conversation) => (
                            <ListItemButton
                                key={conversation.id}
                                selected={selectedId === conversation.id}
                                onClick={() => onSelect(conversation.id)}
                            >
                                <ListItemText
                                    primary={conversation.title || 'گفتگوی بدون عنوان'}
                                    secondary={STATUS_LABELS[conversation.status] || conversation.status}
                                    primaryTypographyProps={{ noWrap: true }}
                                />
                                {conversation.unread_count > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={toPersianDigits(String(conversation.unread_count))}
                                        sx={{ mr: 1, minWidth: 28 }}
                                    />
                                )}
                            </ListItemButton>
                        ))
                    )}
                </List>
            )}
        </Box>
    );
}

function SupportChat() {
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [sending, setSending] = useState(false);
    const [escalating, setEscalating] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    const {
        data: listData,
        loading: listLoading,
        refetch: refetchList,
    } = useFetch(API.aiConversations);

    const detailUrl = selectedId ? `${API.aiConversations}${selectedId}/` : null;
    const {
        data: activeConversation,
        loading: detailLoading,
        refetch: refetchDetail,
    } = useFetch(detailUrl, { skip: !selectedId });

    // Live-ish updates: quietly refresh the list (unread badges) and the open
    // thread so staff replies appear without a manual reload.
    usePolling(() => {
        refetchList({ silent: true });
        if (selectedId) {
            refetchDetail({ silent: true });
        }
    }, POLL_INTERVAL_MS);

    // Fetching a thread stamps it read server-side; refresh the badges to match
    // (both the in-chat list and the Header icon). Keyed on updated_at so
    // replies arriving while the thread is open also clear the badge.
    useEffect(() => {
        if (activeConversation?.id) {
            refetchList({ silent: true });
            window.dispatchEvent(new Event('support-badge-sync'));
        }
    }, [activeConversation?.id, activeConversation?.updated_at, refetchList]);

    const attachmentPreview = useMemo(
        () => (attachment ? URL.createObjectURL(attachment) : null),
        [attachment],
    );
    useEffect(() => {
        return () => {
            if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
        };
    }, [attachmentPreview]);

    const conversations = listData?.results || listData || [];
    const totalUnread = conversations.reduce(
        (sum, conversation) => sum + (conversation.unread_count || 0),
        0,
    );
    const messages = activeConversation?.messages || [];
    const isEscalated = activeConversation?.status === 'escalated';
    const canSend = activeConversation
        ? !['resolved', 'closed'].includes(activeConversation.status)
        : true;

    const showTypingIndicator = sending && activeConversation?.status === 'ai_handling';

    const handleSelectConversation = useCallback((id) => {
        setSelectedId(id);
        setDrawerOpen(false);
    }, []);

    const handleNewChat = useCallback(() => {
        setSelectedId(null);
        setDraft('');
        setAttachment(null);
        setDrawerOpen(false);
    }, []);

    const handleAttachmentChange = useCallback((event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setSnackbar({ open: true, message: IMAGE_TOO_LARGE_MESSAGE, severity: 'error' });
            return;
        }
        setAttachment(file);
    }, []);

    const handleSend = useCallback(async () => {
        const message = draft.trim();
        if ((!message && !attachment) || sending) return;

        setSending(true);
        setSnackbar((prev) => ({ ...prev, open: false }));

        try {
            let payload = { message: toEnglishDigits(message) };
            if (attachment) {
                payload = new FormData();
                payload.append('message', toEnglishDigits(message));
                payload.append('image', attachment);
            }

            if (!selectedId) {
                const response = await api.post(API.aiConversations, payload, {
                    timeout: AI_REQUEST_TIMEOUT_MS,
                });
                setSelectedId(response.data.id);
                await refetchList();
            } else {
                await api.post(
                    `${API.aiConversations}${selectedId}/messages/`,
                    payload,
                    { timeout: AI_REQUEST_TIMEOUT_MS },
                );
                await refetchDetail();
                await refetchList();
            }

            setDraft('');
            setAttachment(null);
        } catch (err) {
            setSnackbar({
                open: true,
                message: extractApiError(err),
                severity: 'error',
            });
            if (selectedId) {
                await refetchDetail();
            }
        } finally {
            setSending(false);
        }
    }, [draft, attachment, sending, selectedId, refetchList, refetchDetail]);

    const handleEscalate = useCallback(async () => {
        if (!selectedId || escalating) return;

        setEscalating(true);
        try {
            await api.post(`${API.aiConversations}${selectedId}/escalate/`, {});
            await refetchDetail();
            await refetchList();
            setSnackbar({
                open: true,
                message: 'این گفتگو به پشتیبان ارجاع شد.',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: extractApiError(err),
                severity: 'error',
            });
        } finally {
            setEscalating(false);
        }
    }, [selectedId, escalating, refetchDetail, refetchList]);

    const listPanel = (
        <ConversationListPanel
            conversations={conversations}
            loading={listLoading}
            selectedId={selectedId}
            onSelect={handleSelectConversation}
            onNewChat={handleNewChat}
        />
    );

    const threadTitle = useMemo(() => {
        if (!selectedId) return 'پشتیبان هوشمند برنا';
        return activeConversation?.title || 'گفتگو';
    }, [selectedId, activeConversation?.title]);

    return (
        <RTL>
            <Box sx={{ p: { xs: 1.5, md: 3 }, height: '100%' }}>
                <Paper sx={{ height: '75vh', minHeight: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <SmartToyOutlinedIcon color="primary" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" noWrap>
                                    {threadTitle}
                                </Typography>
                                {activeConversation?.status && (
                                    <Typography variant="caption" color="text.secondary">
                                        {STATUS_LABELS[activeConversation.status]}
                                    </Typography>
                                )}
                            </Box>
                            {selectedId && activeConversation?.status !== 'escalated' && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={handleEscalate}
                                    disabled={escalating}
                                >
                                    {escalating ? 'در حال ارجاع...' : 'گفتگو با پشتیبان انسانی'}
                                </Button>
                            )}
                            <IconButton
                                color="primary"
                                onClick={handleNewChat}
                                aria-label="گفتگوی جدید"
                                title="گفتگوی جدید"
                            >
                                <AddCommentOutlinedIcon />
                            </IconButton>
                            <IconButton
                                color="primary"
                                onClick={() => setDrawerOpen(true)}
                                aria-label="تاریخچه گفتگوها"
                                title="تاریخچه گفتگوها"
                            >
                                <Badge
                                    badgeContent={totalUnread > 0 ? toPersianDigits(String(totalUnread)) : 0}
                                    color="error"
                                >
                                    <HistoryIcon />
                                </Badge>
                            </IconButton>
                        </Box>

                        {isEscalated && (
                            <Alert severity="info" sx={{ borderRadius: 0 }}>
                                این گفتگو به پشتیبان ارجاع شد
                            </Alert>
                        )}

                        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
                            {!selectedId ? (
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <SmartToyOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                    <Typography color="text.secondary" align="center">
                                        سوال خود را بنویسید تا گفتگو با پشتیبان هوشمند برنا آغاز شود.
                                    </Typography>
                                </Box>
                            ) : detailLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                                    {messages.map((message) => (
                                        <MessageBubble key={message.id} message={message} />
                                    ))}
                                    {showTypingIndicator && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    bgcolor: 'background.neutral',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <CircularProgress size={16} />
                                                <Typography variant="body2" color="text.secondary">
                                                    در حال نوشتن...
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                            {!canSend && selectedId && (
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                    این گفتگو بسته شده و امکان ارسال پیام جدید وجود ندارد.
                                </Alert>
                            )}
                            {attachment && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Box
                                        component="img"
                                        src={attachmentPreview}
                                        alt="پیش‌نمایش تصویر"
                                        sx={{ height: 56, borderRadius: 1, border: 1, borderColor: 'divider' }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
                                        {attachment.name}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setAttachment(null)} aria-label="حذف تصویر">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <IconButton
                                    color="primary"
                                    onClick={handleSend}
                                    disabled={!canSend || sending || (!draft.trim() && !attachment)}
                                    aria-label="ارسال پیام"
                                >
                                    {sending ? <CircularProgress size={22} /> : <SendIcon />}
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    component="label"
                                    disabled={!canSend || sending}
                                    aria-label="پیوست تصویر"
                                    title="پیوست تصویر"
                                >
                                    <ImageOutlinedIcon />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleAttachmentChange}
                                    />
                                </IconButton>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={4}
                                    placeholder="پیام خود را بنویسید..."
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    disabled={!canSend || sending}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                />
                            </Box>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    // Keep the drawer below the fixed 4rem header so its top
                    // (the "new chat" button) isn't hidden behind the AppBar.
                    PaperProps={{ sx: { width: 300, top: '4rem', height: 'calc(100% - 4rem)' } }}
                >
                    {listPanel}
                </Drawer>

                <FloatingSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                />
            </Box>
        </RTL>
    );
}

export default SupportChat;
